import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const LINKS = [
  { label: "About Us", href: "#about", testId: "nav-about-link" },
  { label: "Core Verticals", href: "#verticals", testId: "nav-verticals-link" },
  { label: "Contact Us", href: "#contact", testId: "nav-contact-link" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -100, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      <div
        className={`w-full bg-white border-b border-navy/10 transition-[box-shadow] duration-500 ${
          scrolled
            ? "shadow-[0_16px_48px_rgba(10,16,36,0.14)]"
            : "shadow-[0_10px_36px_rgba(10,16,36,0.08)]"
        }`}
      >
        <div className="h-[88px] max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-[auto_1fr_auto] items-center gap-3">
          <a
            href="#top"
            data-testid="header-logo-link"
            onClick={(e) => go(e, "#top")}
            className="flex items-center shrink-0"
          >
            <img
              src="/assets/logo-en.png"
              alt="Kritajna Ventures LLP"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </a>

          <nav className="hidden md:flex items-center justify-center gap-8" data-testid="header-nav">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testId}
                onClick={(e) => go(e, l.href)}
                className="group relative text-sm font-semibold tracking-wide text-navy hover:text-navy/70 transition-colors duration-300"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-violet to-magenta transition-[width] duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 justify-self-end">
            <button
              data-testid="get-in-touch-button"
              onClick={(e) => go(e, "#contact")}
              className="group hidden md:inline-flex items-center gap-2 rounded-full bg-brand-gradient px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
            >
              Contact Us
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 text-navy"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="md:hidden overflow-hidden border-t border-navy/10"
            >
              <div className="px-6 py-6 flex flex-col gap-5">
                {LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    data-testid={`mobile-${l.testId}`}
                    onClick={(e) => go(e, l.href)}
                    className="text-lg font-medium text-navy"
                  >
                    {l.label}
                  </a>
                ))}
                <button
                  data-testid="mobile-get-in-touch-button"
                  onClick={(e) => go(e, "#contact")}
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-6 py-3 text-sm font-semibold text-white"
                >
                  Contact Us
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
